import { makeStyles } from "@mui/styles";
import { DataGrid } from "@mui/x-data-grid";
import { useState } from "react";
import { E3M3_TO_MCF, M3_TO_BBL } from "../helpers/conversionFactors";
import { useSelector } from "react-redux";
import { selectCDGRJobs } from "../reducers/cdgrJobSlice";

const MONTH_LENGTH = 30.436875;

const useStyles = makeStyles({
  root: {
    background: 'transparent',
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    // padding: '10px',
    overflow: 'hidden',
    // margin: 10
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    minHeight: '100px',
    height: 700,
    marginTop: 20,
    marginBottom: 20,
  },
});

export const ViewCDGRJobTable = ({ demoUser, currentTab, currentUwi, currentTypeCurveName, setCurrentUwi, setCurrentTypeCurveName, showCheckedWells }) => {

  const classes = useStyles();

  const [filterModel, setFilterModel] = useState({
    items: []
  });

  const cdgrJobs = useSelector(selectCDGRJobs);
  const cdgrJob = cdgrJobs?.byId[cdgrJobs?.currentCDGRJobId];

  let rows = [];

  if (currentTab === 0) { // if QC only
    let uwis = [...cdgrJob?.qcFlags];
    if (!showCheckedWells) {
      uwis = uwis.filter(uwi => cdgrJob?.checkedWells?.indexOf(uwi) === -1);
    };
    uwis = uwis?.sort();
    for (let uwi of uwis) {
      const well = cdgrJob?.wells?.find(w => w.uwi === uwi);
      const typeCurve = cdgrJob?.typeCurves?.find(t => t.name === well?.typeCurveName);
      const tcEurGas = typeCurve?.production?.previousProduction?.byFluid?.gas?.slice(-1)?.[0]?.cumVolume;
      const tcEurCondensate = typeCurve?.production?.previousProduction?.byFluid?.condensate?.slice(-1)?.[0]?.cumVolume;

      rows.push({
        id: uwi,
        uwi: demoUser ? 'Demo well' : uwi,
        name: demoUser ? 'Demo well' : (well?.completions?.['Well_Name'] || 'n/a'),
        license: demoUser ? 'Demo well' : (well?.details?.license_number || 'n/a'),
        licensee: demoUser ? 'Demo well' : (well?.details?.licensee || 'n/a'),
        formation: well?.details?.producing_formation || 'n/a',
        typeCurve: demoUser ? 'Demo type curve' : (well?.typeCurveName || 'n/a'),
        totp: well?.completions?.['TOTP_MH']?.split('T')?.[0] || 'n/a',
        deltaGas: well?.job?.byFluid?.gas?.delta,
        deltaCondensate: well?.job?.byFluid?.condensate?.delta,
        deltaTCGas: (well?.job?.byFluid?.gas?.prevEur?.p50 > 0 && tcEurGas > 0) ? ((well?.job?.byFluid?.gas?.prevEur?.p50 - tcEurGas) / tcEurGas * 100)?.toFixed(1)+'%' : 'n/a',
        deltaTCCondensate: (well?.job?.byFluid?.condensate?.prevEur?.p50 > 0 && tcEurCondensate > 0) ? ((well?.job?.byFluid?.condensate?.prevEur?.p50 - tcEurCondensate) / tcEurCondensate * 100)?.toFixed(1)+'%' : 'n/a',
        lastGas: Number(((well?.production?.byFluid?.gas?.slice(-1)?.[0]?.volume / MONTH_LENGTH || 0) * E3M3_TO_MCF / 1000)?.toFixed(2)),
        lastCondensate: Number(((well?.production?.byFluid?.condensate?.slice(-1)?.[0]?.volume / MONTH_LENGTH || 0) * M3_TO_BBL)?.toFixed(2))
      })
    };
    
  } else if (currentTab === 1) { // if all
    let uwis = cdgrJob?.wells?.map(w => w.uwi);
    if (!showCheckedWells) {
      uwis = uwis.filter(uwi => cdgrJob?.checkedWells?.indexOf(uwi) === -1);
    };
    uwis = uwis.sort();

    for (let uwi of uwis) {
      const d = cdgrJob?.wells?.find(w => w.uwi === uwi);
      const typeCurve = cdgrJob?.typeCurves?.find(t => t.name === d?.typeCurveName);
      const tcEurGas = typeCurve?.production?.previousProduction?.byFluid?.gas?.slice(-1)?.[0]?.cumVolume;
      const tcEurCondensate = typeCurve?.production?.previousProduction?.byFluid?.condensate?.slice(-1)?.[0]?.cumVolume;
      
      rows.push({
        id: d.uwi,
        uwi: demoUser ? 'Demo well' : d.uwi,
        name: demoUser ? 'Demo well' : (d?.completions?.['Well_Name'] || 'n/a'),
        license: demoUser ? 'Demo well' : (d.details?.license_number || 'n/a'),
        licensee: demoUser ? 'Demo well' : (d.details?.licensee || 'n/a'),
        formation: d.details?.producing_formation || 'n/a',
        typeCurve: demoUser ? 'Demo type curve' : (d.typeCurveName || 'n/a'),
        totp: d?.completions?.['TOTP_MH']?.split('T')?.[0] || 'n/a',
        deltaGas: d?.job?.byFluid?.gas?.delta,
        deltaCondensate: d?.job?.byFluid?.condensate?.delta,
        deltaTCGas: (d?.job?.byFluid?.gas?.prevEur?.p50 > 0 && tcEurGas > 0) ? ((d?.job?.byFluid?.gas?.prevEur?.p50 - tcEurGas) / tcEurGas * 100)?.toFixed(1)+'%' : 'n/a',
        deltaTCCondensate: (d?.job?.byFluid?.condensate?.prevEur?.p50 > 0 && tcEurCondensate > 0) ? ((d?.job?.byFluid?.condensate?.prevEur?.p50 - tcEurCondensate) / tcEurCondensate * 100)?.toFixed(1)+'%' : 'n/a',
        lastGas: Number(((d?.production?.byFluid?.gas?.slice(-1)?.[0]?.volume / MONTH_LENGTH || 0) * E3M3_TO_MCF / 1000)?.toFixed(2)),
        lastCondensate: Number(((d?.production?.byFluid?.condensate?.slice(-1)?.[0]?.volume / MONTH_LENGTH || 0) * M3_TO_BBL)?.toFixed(2))
      })
    };
  } else if (currentTab === 2) { // if type curves
    let typeCurves = cdgrJob?.typeCurves?.map(t => t.name);
    typeCurves = typeCurves.sort();

    for (let name of typeCurves) {
      const d = cdgrJob?.typeCurves?.find(t => t.name === name);
      rows.push({
        id: d.id,
        uwi: demoUser ? 'Demo type curve' : (d.name || 'n/a'),
        name: demoUser ? 'Demo type curve' : (d.name || 'n/a'),
        license: 'n/a',
        licensee: 'n/a',
        formation: 'n/a',
        typeCurve: demoUser ? 'Demo type curve' : (d.name || 'n/a'),
        totp: 'n/a',
        deltaGas: d?.job?.byFluid?.gas?.delta,
        deltaCondensate: d?.job?.byFluid?.condensate?.delta,
        deltaTCGas: 'n/a',
        deltaTCCondensate: 'n/a',
        lastGas: 0,
        lastCondensate: 0
      })
    };
  } else if (currentTab === 3) { // if group well
    const groupWell = cdgrJob?.groupWell;
    rows.push({
      id: groupWell?.id,
      uwi: demoUser ? 'Demo group well' : (groupWell.name || 'n/a'),
      name: demoUser ? 'Demo group well' : (groupWell.name || 'n/a'),
      license: 'n/a',
      licensee: 'n/a',
      formation: 'n/a',
      typeCurve: 'n/a',
      totp: 'n/a',
      deltaGas: 'n/a',
      deltaCondensate: 'n/a',
      deltaTCGas: 'n/a',
      deltaTCCondensate: 'n/a',
      lastGas: 0,
      lastCondensate: 0
    });
  } else if (currentTab === 4) { // if recent shut-ins
    const uwis = [...cdgrJob?.recentShutIns]?.sort();
    for (let uwi of uwis) {
      const well = cdgrJob?.wells?.find(w => w.uwi === uwi);
      rows.push({
        id: uwi,
        uwi: demoUser ? 'Demo well' : uwi,
        name: demoUser ? 'Demo well' : (well?.completions?.['Well_Name'] || 'n/a'),
        license: demoUser ? 'Demo well' : (well?.details?.license_number || 'n/a'),
        licensee: demoUser ? 'Demo well' : (well?.details?.licensee || 'n/a'),
        formation: well?.details?.producing_formation || 'n/a',
        typeCurve: demoUser ? 'Demo type curve' : (well?.typeCurveName || 'n/a'),
        totp: well?.completions?.['TOTP_MH']?.split('T')?.[0] || 'n/a',
        deltaGas: null,
        deltaCondensate: null,
        deltaTCGas: 'n/a',
        deltaTCCondensate: 'n/a',
        lastGas: 0,
        lastCondensate: 0
      })
    };
  } else if (currentTab === 5) { // if non-producers
    let uwis = cdgrJob?.nonProducing?.map(w => w.uwi);
    uwis = uwis?.sort();
    for (let uwi of uwis) {
      const well = cdgrJob?.nonProducing?.find(w => w.uwi === uwi);
      rows.push({
        id: well?.uwi,
        uwi: demoUser ? 'Demo well' : well?.uwi,
        name: demoUser ? 'Demo well' : (well?.completions?.['Well_Name'] || 'n/a'),
        license: demoUser ? 'Demo well' : (well?.details?.license_number || 'n/a'),
        licensee: demoUser ? 'Demo well' : (well?.details?.licensee || 'n/a'),
        formation: well?.details?.producing_formation || 'n/a',
        typeCurve: demoUser ? 'Demo type curve' : (well?.typeCurveName || 'n/a'),
        totp: well?.completions?.['TOTP_MH']?.split('T')?.[0] || 'n/a',
        deltaGas: null,
        deltaCondensate: null,
        deltaTCGas: 'n/a',
        deltaTCCondensate: 'n/a',
        lastGas: 0,
        lastCondensate: 0
      })
    };
  };

  try {
    const columns = [
      {
        field: 'uwi',
        headerName: 'UWI',
        width: 160,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 120,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'license',
        headerName: 'License',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'formation',
        headerName: 'Formation',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'typeCurve',
        headerName: 'Type Curve',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'totp',
        headerName: 'TOTP_MH',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'deltaGas',
        headerName: 'Gas (%)',
        width: 75,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: false,
        renderCell: (params) => (
          (isNaN(params?.value) || params?.value === null) ? 'n/a' : params?.value >= 0 && params?.value <= 100 ? params?.value?.toFixed(1)?.concat('%') : params?.value > 100 ? '>100%' : 'n/a'
        ),
      },
      {
        field: 'deltaCondensate',
        headerName: 'Cond. (%)',
        width: 75,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: false,
        renderCell: (params) => (
          (isNaN(params?.value) || params?.value === null) ? 'n/a' : params?.value >= 0 && params?.value <= 100 ? params?.value?.toFixed(1)?.concat('%') : params?.value > 100 ? '>100%' : 'n/a'
        ),
      },
      {
        field: 'lastGas',
        headerName: 'Gas Ave. (mmscf/d)',
        width: 75,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: false
      },
      {
        field: 'lastCondensate',
        headerName: 'Cond. Ave. (stb/d)',
        width: 75,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: false
      },
      {
        field: 'deltaTCGas',
        headerName: 'TC Gas (%)',
        width: 75,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: false
      },
      {
        field: 'deltaTCCondensate',
        headerName: 'TC Cond. (%)',
        width: 75,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: false
      }
    ];

    return (
      <DataGrid
        sx={{
          '& .super-app-theme--Current': {
            backgroundColor: 'rgba(7, 88, 183, 0.1)' // highlight the current row
          },
          '& .super-app-theme--QC': {
            color: '#f44336' // highlight the qc row
          },
          '& .super-app-theme--Checked': {
            color: 'lightgrey' // highlight checked row
          },
        }}
        rows={rows}
        columns={columns}
        rowsPerPageOptions={[10,25,50,100]}
        className={classes.table}
        onCellClick={(params) => {
          (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? setCurrentUwi(params.row?.uwi) : 
          currentTab === 2 ? setCurrentTypeCurveName(params.row?.uwi) : (() => {})();
        }}
        getRowClassName={(params) => {
          return (params.row.uwi === currentUwi || params.row.uwi === currentTypeCurveName || currentTab === 3) ? `super-app-theme--Current` :
          (cdgrJob?.checkedWells.includes(params.row.uwi)) ? `super-app-theme--Checked` :
          (currentTab === 0 || currentTab === 1) && !cdgrJob?.checkedWells.includes(params.row.uwi) && (params.row.deltaGas > cdgrJob?.wellErrorThreshold || params.row.deltaCondensate > cdgrJob?.wellErrorThreshold) ? `super-app-theme--QC` : 
          currentTab === 2 && (params.row.deltaGas > cdgrJob?.typeCurveErrorThreshold || params.row.deltaCondensate > cdgrJob?.typeCurveErrorThreshold) ? `super-app-theme--QC` :
          '';
        }}
        filterModel={filterModel}
        onFilterModelChange={newFilterModel => setFilterModel(newFilterModel)}
        disableSelectionOnClick
      />
    );
  } catch (error) {
    return <></>
  };
};

